<template lang="pug">
.package-size(@click.stop="")
  span.label-declared.package-label(@click="openModal") {{ label }}
  button.package-dimensions-summary(
    v-if="dimensionsLabel"
    type="button"
    @click="openModal"
  ) {{ dimensionsLabel }}

  .package-dimensions-modal(v-if="isOpen")
    .package-dimensions-modal__backdrop(@click="cancel")
    .package-dimensions-modal__card
      .package-quantity(v-if="multiple")
        span.package-quantity__label Paquetes
        .package-quantity__control
          button.package-quantity__button(type="button" :disabled="forms.length <= 1" @click="changeQuantity(-1)") −
          span.package-quantity__value {{ forms.length }}
          button.package-quantity__button(type="button" :disabled="forms.length >= maxPackages" @click="changeQuantity(1)") +

      .package-block(v-for="(item, index) in forms" :key="index")
        p.package-block__title(v-if="multiple && forms.length > 1") Paquete {{ index + 1 }}
        .package-options.package-options--modal
          button.package-option(
            v-for="pack in packagesWithCustom"
            :key="pack"
            type="button"
            :class="{ active: item.package === pack }"
            @click="selectPackage(index, pack)"
          ) {{ pack.toUpperCase() }}
        .form-section.package-dimensions-modal__inputs(v-if="item.package === 'otro'")
          .input-container
            input.input(v-model.number="item.alto" type="number" min="1" step="1" autocomplete="ño" required)
            label.label(:class="{ show: item.alto }") Alto (cm)
          .input-container
            input.input(v-model.number="item.ancho" type="number" min="1" step="1" autocomplete="ño" required)
            label.label(:class="{ show: item.ancho }") Ancho (cm)
          .input-container
            input.input(v-model.number="item.largo" type="number" min="1" step="1" autocomplete="ño" required)
            label.label(:class="{ show: item.largo }") Largo (cm)
          .input-container
            input.input(v-model.number="item.peso" type="number" min="1" step="1" autocomplete="ño" required)
            label.label(:class="{ show: item.peso }") Peso (kg)

      .package-dimensions-modal__actions
        shActionButton(v-if="needsSave" :action="save" text="Guardar" textRunning="Guardando" :normalColor="true")
        shActionButton(:action="cancel" text="Cancelar" textRunning="Cancelando")
</template>

<script>
import shActionButton from '@/components/elements/ActionButton.vue'
import { packageLabel, packageDimensionsLabel } from '@/helpers/packageSize'

export default {
  name: 'PackageSize',

  components: { shActionButton },

  props: {
    order: {
      type: Object,
      required: true
    },
    opened: {
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      localOpen: false,
      previousPackage: '',
      maxPackages: 10,
      forms: []
    }
  },

  computed: {
    packages () {
      return this.$store.state.variables.packages || []
    },

    packagesWithCustom () {
      return this.packages.concat(['otro'])
    },

    isControlled () {
      return this.opened !== null
    },

    isOpen () {
      return this.isControlled ? !!this.opened : this.localOpen
    },

    label () {
      return packageLabel(this.order)
    },

    dimensionsLabel () {
      return packageDimensionsLabel(this.order)
    },

    needsSave () {
      if (this.forms.length > 1) return true
      return this.forms.some(item => item.package === 'otro')
    }
  },

  watch: {
    isOpen (value) {
      if (value) this.hydrateForm()
    }
  },

  methods: {
    emptyForm (pack = '') {
      return {
        package: pack,
        alto: '',
        ancho: '',
        largo: '',
        peso: ''
      }
    },

    formFromItem (item = {}) {
      const dimensions = item.packageDimensions || {}
      return {
        package: item.package || '',
        alto: dimensions.alto || '',
        ancho: dimensions.ancho || '',
        largo: dimensions.largo || '',
        peso: dimensions.peso || ''
      }
    },

    hydrateForm () {
      const saved = Array.isArray(this.order.packages) && this.order.packages.length > 1
        ? this.order.packages
        : [{ package: this.order.package, packageDimensions: this.order.packageDimensions }]

      this.previousPackage = this.order.package
      this.forms = saved.map(item => this.formFromItem(item))
    },

    setOpen (value) {
      if (this.isControlled) {
        this.$emit('update:opened', value)
        return
      }

      this.localOpen = value
    },

    applyChange (patch) {
      Object.keys(patch).forEach(key => {
        this.$set(this.order, key, patch[key])
      })

      this.$set(this.order, 'coordinadoraCost', null)
      this.$emit('change', this.order)
    },

    openModal () {
      this.hydrateForm()
      this.setOpen(true)
    },

    close () {
      this.setOpen(false)
    },

    changeQuantity (delta) {
      const next = this.forms.length + delta
      if (next < 1 || next > this.maxPackages) return

      if (delta > 0) {
        const last = this.forms[this.forms.length - 1] || this.emptyForm(this.order.package)
        this.forms.push({ ...last })
        return
      }

      this.forms.pop()
    },

    dimensionsFromForm (item) {
      return {
        alto: parseInt(item.alto, 10),
        ancho: parseInt(item.ancho, 10),
        largo: parseInt(item.largo, 10),
        peso: parseInt(item.peso, 10)
      }
    },

    selectPackage (index, pack) {
      const current = this.forms[index] || this.emptyForm()
      this.$set(this.forms, index, { ...current, package: pack })

      if (pack === 'otro' || this.forms.length > 1) return

      const patch = {
        package: pack,
        packageDimensions: null
      }
      if (this.multiple) patch.packages = null
      this.applyChange(patch)
      this.close()
    },

    save () {
      const packages = []

      for (let index = 0; index < this.forms.length; index += 1) {
        const item = this.forms[index]
        if (!item.package) {
          this.$set(this.order, 'error', 'Elige el tamaño de cada paquete')
          return true
        }

        if (item.package !== 'otro') {
          packages.push({ package: item.package, packageDimensions: null })
          continue
        }

        const dimensions = this.dimensionsFromForm(item)
        if (!dimensions.alto || !dimensions.ancho || !dimensions.largo || !dimensions.peso) {
          const which = this.forms.length > 1 ? ` del paquete ${index + 1}` : ''
          this.$set(this.order, 'error', `Completa alto, ancho, largo y peso${which}`)
          return true
        }

        packages.push({ package: 'otro', packageDimensions: dimensions })
      }

      this.$set(this.order, 'error', '')

      if (packages.length === 1) {
        const patch = {
          package: packages[0].package,
          packageDimensions: packages[0].packageDimensions
        }
        if (this.multiple) patch.packages = null
        this.applyChange(patch)
        this.close()
        this.$emit('saved', { order: this.order, packageDimensions: packages[0].packageDimensions })
        return true
      }

      this.applyChange({
        package: packages[0].package,
        packageDimensions: null,
        packages
      })
      this.close()
      this.$emit('saved', { order: this.order, packages })
      return true
    },

    cancel () {
      if (this.order.package === 'otro' && !this.order.packageDimensions && this.previousPackage && this.previousPackage !== 'otro') {
        const patch = {
          package: this.previousPackage,
          packageDimensions: null
        }
        if (this.multiple) patch.packages = null
        this.applyChange(patch)
      }

      this.close()
      return true
    }
  }
}
</script>

<style lang="stylus" scoped>
.package-size
  display flex
  flex-direction column
  align-items flex-start

.package-label
  cursor pointer
  display inline-block
  min-width 4.5rem
  text-align center

.package-dimensions-summary
  margin-top .25rem
  padding 0
  border none
  background transparent
  color var(--color-placeholder)
  font-size .78rem
  line-height 1.15
  text-align left
  cursor pointer
  text-transform lowercase
  white-space normal

  &:hover
    color var(--color-font-principal)
    text-decoration underline

.package-options
  display flex
  flex-wrap wrap
  gap .25rem

.package-option
  border none
  border-radius .35rem
  padding .2rem .45rem
  background var(--color-light, rgba(0, 0, 0, .06))
  color var(--color-font-principal)
  font-size .78rem
  text-transform uppercase
  cursor pointer

  &.active
    background var(--color-font-principal)
    color var(--background-principal)

.package-options--modal
  gap .75rem
  margin-top 0
  margin-bottom 0

.package-options--modal .package-option
  min-width 5rem
  min-height 2.4rem
  padding .55rem .85rem
  font-size 1rem

.package-quantity
  display flex
  align-items center
  justify-content space-between
  gap 1rem
  margin-bottom 1.5rem

.package-quantity__label
  font-size 1rem
  text-transform uppercase
  color var(--color-placeholder)

.package-quantity__control
  display flex
  align-items center
  gap .75rem

.package-quantity__button
  width 2.4rem
  height 2.4rem
  border none
  border-radius .35rem
  background var(--color-light, rgba(0, 0, 0, .06))
  color var(--color-font-principal)
  font-size 1.25rem
  line-height 1
  cursor pointer

  &:disabled
    opacity .35
    cursor default

.package-quantity__value
  min-width 1.5rem
  text-align center
  font-size 1.15rem

.package-block + .package-block
  margin-top 1.25rem
  padding-top 1.25rem
  border-top 1px solid rgba(0, 0, 0, .08)

.package-block__title
  margin 0 0 .75rem
  font-size .85rem
  text-transform uppercase
  color var(--color-placeholder)

.package-dimensions-modal
  position fixed
  inset 0
  z-index 50
  display flex
  align-items center
  justify-content center

.package-dimensions-modal__backdrop
  position absolute
  inset 0
  background rgba(0, 0, 0, .35)

.package-dimensions-modal__card
  position relative
  z-index 1
  width 36rem
  max-width calc(100vw - 2rem)
  max-height calc(100vh - 4rem)
  overflow-y auto
  padding 2rem
  border-radius 1rem
  background var(--background-principal)
  box-shadow 0 1rem 3rem rgba(0, 0, 0, .2)

.package-dimensions-modal__inputs
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 1rem
  margin-top 1rem

.package-dimensions-modal__actions
  display flex
  justify-content flex-end
  gap 1rem
  margin-top 1.5rem
</style>



// WEBPACK FOOTER //
// src/components/elements/PackageSize.vue